/* ============================================================
   artvision® · AUTH LAYER
   Steuert snippets/av-auth-layer.liquid (Figma 547:2), das Herz im
   Header ([data-av-collector-link]), Anmelde-Links ([data-av-auth])
   und die Collector-Einträge im Konto-Menü (snippets/av-account-menu.liquid;
   Öffnen und Schließen des Menüs: assets/av-account-menu.js).
   · Natives <dialog> mit showModal(): Seite dahinter ist inert.
     Zusätzlich hält Tab den Fokus im Layer, Esc schließt, der
     Fokus kehrt zum auslösenden Element zurück.
   · Validierung und Fehlertexte kommen aus av-collector-core.js —
     dieselben Regeln und Worte wie im Dashboard.
   · Konto-Menü: abgemeldet „Anmelden“/„Registrieren“ (öffnen den
     Layer), angemeldet „Mein Collector Account“ (Handoff) und „Abmelden“.
   ============================================================ */

import {
  AUTH_REQUEST_EVENT,
  clearPendingAction,
  completeAuthentication,
  isCollectorLoginEnabled,
  isFirebaseLoaded,
  loadFirebase,
  onCollectorChange,
  openDashboard,
  recordConsent,
  requestCollectorAuth,
  sendPasswordReset,
  signInWithEmail,
  signInWithGoogle,
  signOutCollector,
  signUpWithEmail,
  waitForCollector,
  wasSessionExpired
} from 'av-collector-session';
import {
  CONSENT_SAVE_FAILED_MESSAGE,
  FALLBACK_AUTH_ERROR_MESSAGE,
  SIGNED_OUT_MESSAGE,
  buildMarketingConsent,
  createDashboardAction,
  getAuthErrorMessage,
  isEmailInUseError,
  isHiddenPasswordResetError,
  isInvalidCredentialsError,
  isSilentAuthError,
  readAccountMenuState,
  readAuthLinkView,
  validatePasswordReset,
  validateSignIn,
  validateSignUp
} from 'av-collector-core';
import { showCollectorToast } from 'av-collector-toast';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
const SCROLL_LOCK_CLASS = 'av-auth-is-open';
const INVALID_CLASS = 'is-invalid';

const layer = document.querySelector('[data-av-auth-layer]');
let returnFocusTo = null;
let rememberedEmail = '';
let keepPendingActionOnClose = false;

/* ---------- Ansichten -------------------------------------- */

function viewElement(name) {
  return layer.querySelector(`[data-av-auth-view="${name}"]`);
}

function currentView() {
  return layer.querySelector('[data-av-auth-view]:not([hidden])');
}

function showView(name, { errorMessage = null } = {}) {
  const view = viewElement(name);
  if (!view) {
    console.error('Unknown auth layer view', name);
    return;
  }

  layer.querySelectorAll('[data-av-auth-view]').forEach((element) => {
    element.hidden = element !== view;
  });

  clearMessages(view);
  prefillEmail(view);
  layer.setAttribute('aria-labelledby', view.querySelector('[data-av-auth-title]').id);
  if (errorMessage) showNotice(view, errorMessage);
  focusViewStart(view);
}

function focusViewStart(view) {
  const firstEmptyInput = Array.from(view.querySelectorAll('.av-auth__input')).find((input) => !input.value);
  (firstEmptyInput || view.querySelector('[data-av-auth-title]')).focus();
}

function prefillEmail(view) {
  view.querySelectorAll('input[type="email"]').forEach((input) => {
    if (!input.value) input.value = rememberedEmail;
  });
}

/* ---------- Öffnen und Schließen --------------------------- */

function openLayer(viewName, options) {
  if (!layer.open) {
    returnFocusTo = document.activeElement;
    layer.showModal();
    document.documentElement.classList.add(SCROLL_LOCK_CLASS);
    prepareGoogleSignIn();
  }

  showView(viewName, options);
}

/** Esc closes natively; every way of closing ends in onLayerClosed(). */
function closeLayer({ keepPendingAction = false } = {}) {
  keepPendingActionOnClose = keepPendingAction;
  if (layer.open) {
    layer.close();
  } else {
    onLayerClosed();
  }
}

function onLayerClosed() {
  // Closed without signing in: the action that asked for the login is dropped.
  if (!keepPendingActionOnClose) clearPendingAction();
  keepPendingActionOnClose = false;

  // No password or name stays in the page after the layer is gone.
  layer.querySelectorAll('form').forEach((form) => form.reset());
  document.documentElement.classList.remove(SCROLL_LOCK_CLASS);

  if (returnFocusTo && returnFocusTo.isConnected) returnFocusTo.focus();
  returnFocusTo = null;
}

/** Google's popup must open synchronously in the click, so the SDK is loaded as soon as the layer opens. */
function prepareGoogleSignIn() {
  if (isFirebaseLoaded()) {
    setGoogleButtonsEnabled(true);
    return;
  }

  loadFirebase()
    .then(() => setGoogleButtonsEnabled(true))
    .catch((error) => {
      console.error('Firebase could not be loaded for the auth layer', error);
      const view = currentView();
      if (view) showNotice(view, FALLBACK_AUTH_ERROR_MESSAGE);
    });
}

function setGoogleButtonsEnabled(isEnabled) {
  layer.querySelectorAll('[data-av-auth-google]').forEach((button) => {
    button.disabled = !isEnabled;
  });
}

function trapFocus(event) {
  const focusable = Array.from(layer.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.closest('[hidden]')
  );
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/* ---------- Meldungen -------------------------------------- */

function showNotice(view, message) {
  const notice = view.querySelector('[data-av-auth-notice]');
  if (!notice) {
    console.error('Auth layer view without notice element', view.dataset.avAuthView);
    return;
  }

  notice.textContent = message;
  notice.hidden = false;
}

function clearMessages(view) {
  const notice = view.querySelector('[data-av-auth-notice]');
  if (notice) {
    notice.textContent = '';
    notice.hidden = true;
  }

  view.querySelectorAll('form').forEach((form) => applyFieldErrors(form, {}));
}

/** Shows `errors` ({fieldName: message}) and focuses the first invalid field. Returns true when valid. */
function applyFieldErrors(form, errors) {
  let firstInvalid = null;

  form.querySelectorAll('[data-av-auth-field]').forEach((field) => {
    const message = errors[field.dataset.avAuthField] || '';
    const input = field.querySelector('input');
    const errorElement = field.querySelector('[data-av-auth-error]');

    field.classList.toggle(INVALID_CLASS, Boolean(message));
    if (message) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }

    if (errorElement) {
      errorElement.textContent = message;
      errorElement.hidden = !message;
    }

    if (message && !firstInvalid) firstInvalid = input;
  });

  if (firstInvalid) firstInvalid.focus();
  return firstInvalid === null;
}

/* ---------- Formulare -------------------------------------- */

function readForm(form) {
  const values = {};
  form.querySelectorAll('input[name]').forEach((input) => {
    values[input.name] = input.type === 'checkbox' ? input.checked : input.value;
  });
  return values;
}

async function withBusyButton(button, task) {
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    await task();
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

/** Closes the layer and continues with what the visitor wanted to do before signing in. */
async function finishAuthentication() {
  closeLayer({ keepPendingAction: true });

  try {
    await completeAuthentication();
  } catch (error) {
    console.error('Completing the sign-in failed', error);
  }
}

async function submitSignIn(form, view) {
  const values = readForm(form);
  if (!applyFieldErrors(form, validateSignIn(values))) return;

  try {
    await signInWithEmail(values.email, values.password);
  } catch (error) {
    console.error('Collector sign-in failed', error);
    if (isInvalidCredentialsError(error)) {
      applyFieldErrors(form, { password: getAuthErrorMessage(error) });
    } else {
      showNotice(view, getAuthErrorMessage(error));
    }
    return;
  }

  await finishAuthentication();
}

async function submitSignUp(form, view) {
  const values = readForm(form);
  if (!applyFieldErrors(form, validateSignUp(values))) return;

  try {
    await signUpWithEmail(values);
  } catch (error) {
    console.error('Collector sign-up failed', error);
    if (isEmailInUseError(error)) {
      showView('email_in_use');
    } else {
      showNotice(view, getAuthErrorMessage(error));
    }
    return;
  }

  if (await saveMarketingConsent(form)) {
    await finishAuthentication();
    return;
  }

  // The account exists; the collector reads the notice in the layer and
  // continues with "Weiter" — no navigation before the message was seen.
  showView('consent_failed', { errorMessage: CONSENT_SAVE_FAILED_MESSAGE });
}

/**
 * Stores the ticked newsletter box (collectors/{uid}/consents). The stored text
 * is read from the visible label, so both always match; the version sits on the
 * checkbox (snippets/av-auth-layer.liquid). Returns false when it could not be
 * stored — the registration still counts.
 */
async function saveMarketingConsent(form) {
  const checkbox = form.querySelector('[data-av-consent-version]');
  if (!checkbox || !checkbox.checked) return true;

  const label = form.querySelector(`label[for="${checkbox.id}"]`);
  const consent = buildMarketingConsent({
    text: label ? label.textContent : '',
    textVersion: checkbox.dataset.avConsentVersion,
    granted: true
  });

  try {
    if (!consent) throw new Error('Marketing consent text or version is invalid.');
    await recordConsent(consent);
    return true;
  } catch (error) {
    console.error('Marketing consent could not be saved', error);
    return false;
  }
}

async function submitPasswordReset(form, view) {
  const values = readForm(form);
  if (!applyFieldErrors(form, validatePasswordReset(values))) return;

  try {
    await sendPasswordReset(values.email);
  } catch (error) {
    if (!isHiddenPasswordResetError(error)) {
      console.error('Password reset e-mail failed', error);
      showNotice(view, getAuthErrorMessage(error));
      return;
    }
  }

  showView('reset_sent');
}

const FORM_HANDLERS = {
  sign_in: submitSignIn,
  sign_up: submitSignUp,
  password_reset: submitPasswordReset
};

function onSubmit(event) {
  const form = event.target.closest('[data-av-auth-form]');
  const handler = form && FORM_HANDLERS[form.dataset.avAuthForm];
  if (!handler) return;

  event.preventDefault();
  const view = form.closest('[data-av-auth-view]');
  clearMessages(view);
  void withBusyButton(form.querySelector('[type="submit"]'), () => handler(form, view));
}

function startGoogleSignIn(button) {
  const view = button.closest('[data-av-auth-view]');
  clearMessages(view);

  // No await before signInWithGoogle(): the popup must open inside the click.
  void withBusyButton(button, () =>
    signInWithGoogle().then(finishAuthentication, (error) => {
      if (isSilentAuthError(error)) return;
      console.error('Google sign-in failed', error);
      showNotice(view, getAuthErrorMessage(error));
    })
  );
}

/* ---------- Links in den Collector Account ----------------- */
/* [data-av-collector-link="favorites"]  — Herz im Header
   [data-av-auth="sign-in|sign-up"]       — Konto-Menü, Collectors Club;
                                             führt danach ins Konto
   Das href bleibt Rückfall ohne JS oder ohne Firebase.          */

function isPlainLeftClick(event) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/** Which dashboard page the link leads to, and which layer view a signed-out visitor sees. */
function describeCollectorLink(link) {
  if (link.hasAttribute('data-av-auth')) {
    return { target: 'account', signedOutView: readAuthLinkView(link.dataset.avAuth) };
  }

  const target = link.dataset.avCollectorLink;
  return { target, signedOutView: wasSessionExpired() ? 'session_expired' : 'sign_in' };
}

/** Signed in → dashboard via handoff. Signed out → auth layer, then the dashboard. */
async function openCollectorLink(link) {
  link.setAttribute('aria-busy', 'true');

  try {
    const collector = await waitForCollector();
    const { target, signedOutView } = describeCollectorLink(link);

    if (collector) {
      await openDashboard(target);
      return;
    }

    if (!signedOutView) throw new Error(`Unknown data-av-auth value "${link.dataset.avAuth}"`);
    requestCollectorAuth({ view: signedOutView, pendingAction: createDashboardAction(target) });
  } catch (error) {
    console.error('Collector link could not be handled; following the plain link', error);
    window.location.assign(link.href);
  } finally {
    link.removeAttribute('aria-busy');
  }
}

/* ---------- Konto-Menü: angemeldet ------------------------- */
/* Liquid rendert den abgemeldeten Zustand. Sobald Firebase einen
   Collector kennt, zeigen alle Menüs (Header und Drawer) den
   angemeldeten Block. Text nur über textContent.               */

function renderAccountMenus(collector) {
  const { isSignedIn, email } = readAccountMenuState(collector);

  document.querySelectorAll('[data-av-account-signed-in]').forEach((block) => {
    block.hidden = !isSignedIn;
  });
  document.querySelectorAll('[data-av-account-signed-out]').forEach((block) => {
    block.hidden = isSignedIn;
  });
  document.querySelectorAll('[data-av-account-email-line]').forEach((line) => {
    line.hidden = !email;
    line.querySelector('[data-av-account-email]').textContent = email;
  });
}

function openAccountDashboard(button) {
  void withBusyButton(button, async () => {
    try {
      await openDashboard('account');
    } catch (error) {
      console.error('Collector account could not be opened', error);
      showCollectorToast(FALLBACK_AUTH_ERROR_MESSAGE, { tone: 'error' });
    }
  });
}

function signOutFromShop(button) {
  void withBusyButton(button, async () => {
    try {
      await signOutCollector();
    } catch (error) {
      console.error('Collector sign-out failed', error);
      showCollectorToast(FALLBACK_AUTH_ERROR_MESSAGE, { tone: 'error' });
      return;
    }

    showCollectorToast(SIGNED_OUT_MESSAGE);
  });
}

function onDocumentClick(event) {
  const dashboardButton = event.target.closest('[data-av-account-dashboard]');
  if (dashboardButton) {
    openAccountDashboard(dashboardButton);
    return;
  }

  const signOutButton = event.target.closest('[data-av-account-sign-out]');
  if (signOutButton) {
    signOutFromShop(signOutButton);
    return;
  }

  const link = event.target.closest('[data-av-collector-link], [data-av-auth]');
  if (!link || event.defaultPrevented || !isPlainLeftClick(event)) return;

  event.preventDefault();
  void openCollectorLink(link);
}

/* ---------- Ereignisse im Layer ---------------------------- */

function onLayerClick(event) {
  // Clicks outside the panel land on the <dialog> itself, which is the dimmed backdrop.
  if (event.target === layer || event.target.closest('[data-av-auth-close]')) {
    closeLayer();
    return;
  }

  const switcher = event.target.closest('[data-av-auth-show]');
  if (switcher) {
    showView(switcher.dataset.avAuthShow);
    return;
  }

  const googleButton = event.target.closest('[data-av-auth-google]');
  if (googleButton) {
    startGoogleSignIn(googleButton);
    return;
  }

  const continueButton = event.target.closest('[data-av-auth-continue]');
  if (continueButton) void withBusyButton(continueButton, finishAuthentication);
}

function start() {
  if (!layer) {
    console.error('Auth layer markup missing (snippets/av-auth-layer.liquid).');
    return;
  }

  // Header, account menu and Collectors Club links then stay plain links into the dashboard.
  if (!isCollectorLoginEnabled) return;

  layer.addEventListener('click', onLayerClick);
  layer.addEventListener('submit', onSubmit);
  layer.addEventListener('input', (event) => {
    if (event.target.type === 'email') rememberedEmail = event.target.value.trim();
  });
  layer.addEventListener('close', onLayerClosed);
  layer.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') trapFocus(event);
  });

  document.addEventListener('click', onDocumentClick);
  onCollectorChange(renderAccountMenus);
  document.addEventListener(AUTH_REQUEST_EVENT, (event) => {
    openLayer(event.detail.view, { errorMessage: event.detail.errorMessage });
  });
}

start();
